import { useState } from 'react'


export enum PagedComponentState {
  Loading,
  PageLoading,
  Succeed,
  Failed
}

export function usePagedComponentStateMachine() {
  const [current, setState] = useState<PagedComponentState>(PagedComponentState.Loading)

  function canTransition(from: PagedComponentState, to: PagedComponentState) {
    switch (from) {
      case PagedComponentState.Loading:
        return to === PagedComponentState.Succeed || to === PagedComponentState.Failed
      case PagedComponentState.PageLoading:
        return to === PagedComponentState.Succeed || to === PagedComponentState.Failed
      case PagedComponentState.Succeed:
        // Succeed -> Failed is a real transition with keepPreviousData: a
        // query-key change keeps status "success" (previous data shown, so
        // isLoading never goes true) and a failing fetch then flips straight
        // to "error". It also covers a failing background refetch. Without
        // this the render-time dataTransition throws "illegal transition"
        // and the whole route crashes into the router errorElement.
        return to === PagedComponentState.PageLoading || to === PagedComponentState.Failed
      case PagedComponentState.Failed:
        // Failed -> Succeed: after an error, switching to an already-cached
        // query key yields "success" immediately (isLoading stays false).
        return to === PagedComponentState.PageLoading || to === PagedComponentState.Succeed
    }
  }

  function transitionTo(to: PagedComponentState) {
    if (to === current) {
      return
    }

    if (!canTransition(current, to)) {
      throw new Error('illegal transition')
    }

    setState(to)
  }

  function transitionOrStaySucceeded() {
    transitionTo(PagedComponentState.Succeed)
  }

  function transitionOrStayFailed() {
    transitionTo(PagedComponentState.Failed)
  }

  function transitionOrStayLoading() {
    if (canTransition(current, PagedComponentState.Loading)) {
      transitionTo(PagedComponentState.Loading)
      return
    }
    if (canTransition(current, PagedComponentState.PageLoading)) {
      transitionTo(PagedComponentState.PageLoading)
      return
    }
  }

  function dataTransition(isLoading: boolean, isSucceeded: boolean, isFailed: boolean) {
    if (isSucceeded) {
      transitionOrStaySucceeded()
    } else if (isFailed) {
      transitionOrStayFailed()
    } else if (isLoading) {
      transitionOrStayLoading()
    }
  }

  return {
    state: current,
    transition: transitionTo,
    dataTransition: dataTransition
  }
}